// Camera + microphone permission, asked up front.
//
// Agora only opens the devices when it joins the channel, which is the very end
// of the flow — by then the client has paid and a lawyer has been rung. Asking
// here, before any of that, means a refused camera costs nothing: no card is
// authorized and no lawyer's phone rings for a call that cannot happen.

/** Map a getUserMedia rejection onto something we can explain to the client. */
function reasonFor(err) {
  const name = err && err.name;
  if (name === "NotAllowedError" || name === "PermissionDeniedError") return "denied";
  if (name === "NotFoundError" || name === "DevicesNotFoundError") return "no-device";
  if (name === "NotReadableError" || name === "TrackStartError") return "in-use";
  return "error";
}

/**
 * Prompt for camera + microphone. Resolves { ok: true } once they are granted,
 * or { ok: false, reason } describing why not.
 *
 * The browser only shows its popup the first time; afterwards it answers from
 * the remembered decision, so this is cheap to call on every attempt.
 */
export async function ensureMediaPermission() {
  // Undefined on an insecure origin — getUserMedia needs HTTPS (or localhost).
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return { ok: false, reason: "unsupported" };
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    // Let the devices go again straight away. This call exists only to settle
    // the permission — the call opens its own tracks when it joins, and by then
    // the grant is remembered. Holding them here would sit the camera light on
    // through checkout.
    stream.getTracks().forEach((t) => t.stop());
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: reasonFor(err) };
  }
}

/**
 * What the browser would do if we asked for the camera and mic right now:
 *
 *   "granted" — hand them over without asking
 *   "prompt"  — show its popup (never asked, or the client dismissed it)
 *   "denied"  — refuse outright, and nothing we call can raise the popup again
 *   "unknown" — the browser will not say (Safari does not answer for these)
 *
 * This is the whole difference between "let us ask again" and "you have to
 * unblock us yourself", which are the only two kinds of help we can offer.
 */
export async function getMediaPermissionState() {
  if (!navigator.permissions || !navigator.permissions.query) return "unknown";
  try {
    const states = (
      await Promise.all([
        navigator.permissions.query({ name: "camera" }),
        navigator.permissions.query({ name: "microphone" }),
      ])
    ).map((r) => r.state);
    if (states.includes("denied")) return "denied";
    if (states.every((s) => s === "granted")) return "granted";
    return "prompt";
  } catch {
    // Safari throws on these permission names rather than answering.
    return "unknown";
  }
}

